import { useMemo } from 'react'

import { Heading } from '@hhgtech/hhg-components'
import { AspectRatio, Box, Stack, rem } from '@mantine/core'

import { Container } from 'components/atoms/container'
import { useIsMobile } from 'hooks/useIsMobile'

import useStyles from './index.styles'
import { HeroBannerProps } from '..'
import { MedicalFacts } from '../medicalFacts'
import { Navigator } from '../navigator'
import { BlockItem } from '../navigator/blockItem'

const DefaultBanner = ({
  mappingIconList,
  colorArr,
  sessionProps: {
    imageBanner,
    title,
    description,
    navigating: navigatingInit,
    medicalFacts,
  },
  styles,
  navigatorProps,
}: HeroBannerProps) => {
  const navigating = [...navigatingInit].slice(0, 4) // for test layout
  const blockName = useMemo(
    () => navigatorProps?.itemComponent?.displayName || 'FlatIcon',
    [navigating],
  )
  const { classes } = useStyles(
    {
      numberItem: navigating.length,
      blockName,
    },
    { name: 'CarePathHeroBannerDefaultLayout', styles },
  )

  const isMobile = useIsMobile()
  const desktopBanner = useMemo(() => imageBanner[0]?.desktopBanner, [])
  const mobileBanner = useMemo(() => imageBanner[0]?.mobileBanner, [])
  const ContainerWrapper = isMobile ? Box : AspectRatio
  return (
    <Box>
      <Stack
        sx={(theme) => ({
          gap: rem(16),
          [theme.fn.largerThan('sm')]: {
            gap: rem(40),
          },
        })}
      >
        <Box
          sx={(theme) => ({
            [theme.fn.largerThan('sm')]: {
              position: 'relative',
            },
          })}
        >
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                width: '50%',
                backgroundImage: `url(${desktopBanner})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
                backgroundPosition: 'left',
              }}
            />
          )}
          <ContainerWrapper ratio={1920 / 600}>
            <Box
              sx={(theme) => ({
                [theme.fn.largerThan('sm')]: {
                  display: 'flex',
                  alignItems: 'center',
                },
              })}
            >
              <Box
                sx={(theme) => ({
                  height: '100%',
                  [theme.fn.largerThan('sm')]: { display: 'none' },
                })}
              >
                <AspectRatio
                  ratio={isMobile ? 750 / 208 : 892 / 550}
                  sx={{
                    height: '100%',
                  }}
                >
                  <img
                    src={mobileBanner}
                    alt={title || description}
                    loading="lazy"
                  />
                </AspectRatio>
              </Box>
              {(title || description || navigating.length > 0) && (
                <Container style={{ position: 'relative' }}>
                  <Box
                    sx={(theme) => ({
                      [theme.fn.largerThan('sm')]: {
                        width: rem(552),
                      },
                    })}
                  >
                    <Stack spacing={rem(24)}>
                      <Stack spacing={rem(12)}>
                        {title && <Heading tag="h2">{title}</Heading>}
                        {/* {description && <Text size="p3">{description}</Text>} */}
                      </Stack>
                      <Navigator
                        mappingIconList={mappingIconList}
                        classNames={{
                          root: classes.navigatorRoot,
                          // item: classes.navigatorItem,
                          // itemImgWrapper: classes.navigatorItemImgWrapper,
                          // itemTitle: classes.navigatorItemTitle,
                        }}
                        navigating={navigating}
                        colorArr={colorArr}
                        {...navigatorProps}
                      >
                        {navigating.map((_i, idx) => (
                          <BlockItem
                            key={String(idx)}
                            classNames={{
                              root: classes.navigatorItem,
                            }}
                            displayAs={navigatorProps?.itemComponent}
                            iconSize={
                              blockName !== 'ElegantUI'
                                ? navigating.length > 3
                                  ? 24
                                  : 32
                                : undefined
                            }
                            {...(blockName === 'FlatIcon' && {
                              layout:
                                navigating.length < 3 ? 'horizon' : 'vertical',
                              fullWidth: isMobile && navigating.length < 3,
                            })}
                          />
                        ))}
                      </Navigator>
                    </Stack>
                  </Box>
                </Container>
              )}
            </Box>
          </ContainerWrapper>
        </Box>
        {medicalFacts.listContent.length > 0 && (
          <Container>
            <MedicalFacts
              medicalFacts={medicalFacts}
              sx={(theme) => ({
                background: theme.colors.neutral[0],
              })}
            />
          </Container>
        )}
      </Stack>
    </Box>
  )
}

DefaultBanner.displayName = 'DefaultBanner'

export { DefaultBanner }
